import React, { useContext, useEffect } from 'react'
import { FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import { Radio } from 'antd'
import { PromptGroupPageConditions } from '@pushly/models/lib/structs/prompts/prompt-group-page-conditions'
import { PageUrlsConditionEditor } from './page-conditions/page-urls-condition-editor'
import { MetaKeywordsConditionEditor } from './page-conditions/meta-keywords-condition-editor'
import { PromptGroupConditions } from '@pushly/models/lib/structs/prompts/prompt-group-conditions'
import { PromptEditorDispatchContext } from '../reducer'
import { PromptEditorContext } from '../context'

type PageConditionType = 'ALL_PAGES' | 'PAGE_URLS' | 'META_KEYWORDS'

function getPageConditionType(conditions?: PromptGroupPageConditions) {
    if (conditions) {
        if ('pageUrls' in conditions || 'excludedPageUrls' in conditions) {
            return 'PAGE_URLS'
        }
        if ('keywords' in conditions) {
            return 'META_KEYWORDS'
        }
    }
    return 'ALL_PAGES'
}

export const PromptGroupPageConditionsEditor = () => {
    const dispatch = useContext(PromptEditorDispatchContext)
    const { loading, promptGroup } = useContext(PromptEditorContext)

    const onChange = (conditionsUpdate: PromptGroupPageConditions) => {
        if (!promptGroup) {
            return
        }

        const groupUpdate = promptGroup.clone()
        groupUpdate.conditions ??= new PromptGroupConditions()
        groupUpdate.conditions.page = conditionsUpdate

        dispatch({ type: 'set-prompt-group', group: groupUpdate })
    }

    const pageConditions = promptGroup?.conditions?.page

    const [conditionType, setConditionType] = React.useState<PageConditionType | undefined>(
        promptGroup?.conditions?.page ? getPageConditionType(promptGroup.conditions?.page) : undefined,
    )

    // set conditionType, after loading is completed, if it has not already been set above
    useEffect(() => {
        if (!loading && !conditionType) {
            setConditionType(getPageConditionType(pageConditions))
        }
    }, [loading, conditionType, !!pageConditions])

    return (
        <Well title="Page Conditions" hideFooter={true}>
            <p>
                By default this prompt will be shown on all pages. You may choose to restrict this prompt to a specific
                set of pages by their URL or meta keywords.
            </p>

            <FormLayoutRow
                loading={loading}
                skeleton={{ width: 320 }}
                className="page-condition-type"
                layout="vertical"
                label={null}
                value={
                    !loading && (
                        <Radio.Group
                            size="small"
                            buttonStyle="solid"
                            value={conditionType ?? 'ALL_PAGES'}
                            onChange={(ev) => {
                                const nextCondition: PageConditionType = ev.target.value
                                setConditionType(nextCondition)

                                const update = new PromptGroupPageConditions()
                                switch (nextCondition) {
                                    case 'PAGE_URLS': {
                                        update.pageUrls = null!
                                        break
                                    }
                                    case 'META_KEYWORDS': {
                                        update.keywords = null!
                                        break
                                    }
                                }

                                // reset previous conditions
                                onChange(update)
                            }}
                        >
                            <Radio.Button value="ALL_PAGES">All Pages</Radio.Button>
                            <Radio.Button value="PAGE_URLS">URLs Containing</Radio.Button>
                            <Radio.Button value="META_KEYWORDS">Meta Keywords</Radio.Button>
                        </Radio.Group>
                    )
                }
            />

            {conditionType === 'PAGE_URLS' && <PageUrlsConditionEditor value={pageConditions} onChange={onChange} />}
            {conditionType === 'META_KEYWORDS' && (
                <MetaKeywordsConditionEditor value={pageConditions} onChange={onChange} />
            )}
        </Well>
    )
}

/* webpack-strip-block:removed */
